// src/pages/DetalleIpInvalida.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../services/api";
import logo from "../assets/Logo_DMARCOne.png"; // Logo DMARCONE

// ================= Helpers UI =================
const API_BASE =
  process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000/dmarc";

const classNames = (...xs) => xs.filter(Boolean).join(" ");

const Badge = ({ tone = "default", children, title }) => {
  const tones = {
    success: "bg-green-100 text-green-800 ring-green-200",
    danger: "bg-red-100 text-red-800 ring-red-200",
    warn: "bg-yellow-100 text-yellow-800 ring-yellow-200",
    info: "bg-blue-100 text-blue-800 ring-blue-200",
    default: "bg-slate-100 text-slate-800 ring-slate-200",
    muted: "bg-slate-50 text-slate-500 ring-slate-200",
  };

  return (
    <span
      title={title}
      className={classNames(
        "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset",
        tones[tone] || tones.default,
      )}
    >
      {children}
    </span>
  );
};

const Dot = ({ tone = "muted" }) => (
  <span
    className={classNames(
      "inline-block w-2 h-2 rounded-full mr-1",
      tone === "success" && "bg-green-500",
      tone === "danger" && "bg-red-500",
      tone === "warn" && "bg-yellow-500",
      tone === "info" && "bg-blue-500",
      tone === "muted" && "bg-slate-300",
    )}
  />
);

const Pill = ({ ok, labelTrue = "Alineado", labelFalse = "No alineado" }) => (
  <Badge tone={ok ? "success" : "danger"}>
    <Dot tone={ok ? "success" : "danger"} /> {ok ? labelTrue : labelFalse}
  </Badge>
);

const CopyButton = ({ text, label = "Copiar" }) => {
  const [copied, setCopied] = useState(false);

  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text || "");
          setCopied(true);
          setTimeout(() => setCopied(false), 900);
        } catch {
          /* ignore */
        }
      }}
      className="text-[11px] px-2 py-1 rounded-md ring-1 ring-slate-200 hover:bg-slate-50"
    >
      {copied ? "Copiado" : label}
    </button>
  );
};

// ================= Helpers datos =================
function toBool(v) {
  if (v === true || v === false) return v;
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return ["true", "t", "1", "yes", "y", "ok", "si", "sí", "pass"].includes(s);
}

// Agrupa filas DKIM/SPF por recordid (igual al original)
function groupByRecord(rows) {
  const map = new Map();
  for (const r of rows || []) {
    const k = r.recordid;
    const prev = map.get(k) || { recordid: k, items: [], sample: r };
    prev.items.push(r);
    map.set(k, prev);
  }
  return Array.from(map.values());
}

// ================= Componente =================
export default function DetalleIpInvalida() {
  const location = useLocation();
  const qs = new URLSearchParams(location.search);

  const ip = qs.get("ip") || "";
  const dominio = qs.get("dominio") || "";
  const desde = qs.get("desde") || "";
  const hasta = qs.get("hasta") || "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancel = false;

    async function run() {
      try {
        setLoading(true);
        setErr("");

        const res = await api.get("/detalle_ip_invalida_por_ip", {
          params: { ip, dominio, desde, hasta },
        });

        const data = res.data;
        if (!cancel) {
          setRows(Array.isArray(data) ? data : data?.rows || []);
        }
      } catch (e) {
        if (cancel) return;

        const status = e.response?.status;
        if (status === 403) {
          setErr("No tienes permiso para ver este detalle (HTTP 403).");
        } else if (status === 401) {
          setErr("Sesión expirada o token inválido (HTTP 401).");
        } else {
          setErr(String(e.message || e));
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    }

    if (ip && dominio && desde && hasta) {
      run();
    } else {
      setLoading(false);
      setErr("Faltan parámetros en la URL (ip, dominio, desde, hasta).");
    }

    return () => {
      cancel = true;
    };
  }, [ip, dominio, desde, hasta]);

  // Agrupación por recordid
  const groups = useMemo(() => groupByRecord(rows || []), [rows]);

  // Resumen
  const summary = useMemo(() => {
    const vistos = new Set();
    let regs = 0;
    let msgs = 0;
    let dkimOk = false;
    let spfOk = false;

    for (const r of rows || []) {
      const recId = r.recordid;
      if (!vistos.has(recId)) {
        vistos.add(recId);
        regs += 1;
        msgs += Number(r.count || r.mensajes || 0);
      }

      const authtype = (r.authtype || "").toLowerCase();
      if (authtype === "dkim" && toBool(r.dkimaligned)) dkimOk = true;
      if (authtype === "spf" && toBool(r.spfaligned)) spfOk = true;
    }

    return {
      registros: regs,
      mensajes: msgs,
      anyDkimAligned: dkimOk,
      anySpfAligned: spfOk,
    };
  }, [rows]);

  // Acciones cabecera
  const handleCerrarPestana = () => {
    window.close();
  };

  const handleCopiarUrl = () => {
    try {
      navigator.clipboard.writeText(window.location.href);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F5F5F5" }}>
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
        {/* CABECERA CON LOGO Y BOTONES */}
        <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
            {/* Izquierda: datos de contexto */}
            <div className="space-y-1">
              <h1
                className="text-xl md:text-2xl font-semibold"
                style={{ color: "#414142" }}
              >
                Detalle IP inválida
              </h1>
              <div className="text-xs text-gray-600">
                <span className="font-semibold">IP:</span> {ip || "—"}
              </div>
              <div className="text-xs text-gray-600">
                <span className="font-semibold">Dominio:</span> {dominio || "—"}
              </div>
              <div className="text-xs text-gray-600">
                <span className="font-semibold">Rango:</span> {desde || "—"} →{" "}
                {hasta || "—"}
              </div>
            </div>

            {/* Centro: logo DMARCONE */}
            <div className="flex justify-center">
              <img
                src={logo}
                alt="DMARCONE"
                className="h-10 md:h-12 w-auto opacity-95"
              />
            </div>

            {/* Derecha: acciones */}
            <div className="flex justify-start md:justify-end gap-2">
              <button
                type="button"
                onClick={handleCopiarUrl}
                className="px-3 py-1.5 rounded-lg border text-xs font-medium shadow-sm hover:bg-gray-100"
                style={{ borderColor: "#E5E7EB", color: "#414142" }}
              >
                Copiar URL
              </button>
              <button
                type="button"
                onClick={handleCerrarPestana}
                className="px-3 py-1.5 rounded-lg border text-xs font-medium shadow-sm hover:bg-gray-100"
                style={{ borderColor: "#E5E7EB", color: "#414142" }}
              >
                Cerrar pestaña
              </button>
            </div>
          </div>
        </div>

        {/* ESTADO */}
        {loading && (
          <div className="bg-white border border-gray-200 rounded-2xl p-4 text-sm text-gray-600 shadow-sm">
            Cargando detalle de la IP…
          </div>
        )}

        {err && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-700 shadow-sm">
            Error: {err}
          </div>
        )}

        {/* CONTENIDO */}
        {!loading && !err && (
          <>
            {/* Chips resumen */}
            <div className="flex flex-wrap gap-2">
              <Badge tone="info">Registros: {summary.registros}</Badge>
              <Badge tone="info">Mensajes: {summary.mensajes}</Badge>
              <Badge tone={summary.anyDkimAligned ? "success" : "danger"}>
                DKIM {summary.anyDkimAligned ? "alineado" : "no alineado"}
              </Badge>
              <Badge tone={summary.anySpfAligned ? "success" : "danger"}>
                SPF {summary.anySpfAligned ? "alineado" : "no alineado"}
              </Badge>
            </div>

            {/* Interpretación rápida */}
            <section
              className="rounded-2xl border shadow-sm px-4 py-3 text-sm"
              style={{
                backgroundColor: "#FFF9E6",
                borderColor: "#FDE68A",
                color: "#92400E",
              }}
            >
              <div className="font-semibold mb-1">Interpretación rápida</div>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  DMARC falla cuando ninguna vía logra{" "}
                  <strong>PASS + Alineación</strong>.
                </li>
                <li>
                  Si ves DKIM alineado pero <code>result = fail</code>, revisa
                  el selector o la firma.
                </li>
                <li>
                  Si SPF muestra un dominio distinto al <code>header_from</code>
                  , la alineación será 0.
                </li>
              </ul>
            </section>

            {/* Tabla principal */}
            <section
              className="rounded-2xl shadow p-5"
              style={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #E5E7EB",
              }}
            >
              <h2
                className="text-lg font-medium mb-3"
                style={{ color: "#414142" }}
              >
                Registros asociados a la IP
              </h2>

              <div className="overflow-auto rounded-xl border border-gray-100">
                <table className="min-w-full text-xs md:text-sm">
                  <thead>
                    <tr
                      className="text-left uppercase tracking-wide text-[10px] md:text-[11px]"
                      style={{ backgroundColor: "#00C4CC", color: "#FFFFFF" }}
                    >
                      <th className="px-3 py-2">RecordID</th>
                      <th className="px-3 py-2">Report Org</th>
                      <th className="px-3 py-2">ASN Org</th>
                      <th className="px-3 py-2">Cloud Guess</th>
                      <th className="px-3 py-2">DKIM</th>
                      <th className="px-3 py-2">SPF</th>
                      <th className="px-3 py-2">Selector</th>
                      <th className="px-3 py-2">Causa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.length ? (
                      groups.map(({ recordid, items, sample }, idx) => {
                        const dkimRow = items.find(
                          (r) => (r.authtype || "").toLowerCase() === "dkim",
                        );
                        const spfRow = items.find(
                          (r) => (r.authtype || "").toLowerCase() === "spf",
                        );

                        const dkimAligned = toBool(
                          dkimRow?.dkimaligned ?? sample?.dkimaligned,
                        );
                        const spfAligned = toBool(
                          spfRow?.spfaligned ?? sample?.spfaligned,
                        );

                        const dkimPass =
                          (dkimRow?.result || "").toLowerCase() === "pass";
                        const spfPass =
                          (spfRow?.result || "").toLowerCase() === "pass";

                        const cause = (() => {
                          if (dkimPass && dkimAligned) return "—";
                          if (spfPass && spfAligned) return "—";
                          if (!dkimPass && !spfPass)
                            return "Sin PASS DKIM ni SPF";
                          if (dkimPass && !dkimAligned)
                            return "DKIM PASS no alineado";
                          if (spfPass && !spfAligned)
                            return "SPF PASS no alineado";
                          if (!dkimPass && dkimAligned)
                            return "DKIM FAIL (aunque alineó)";
                          if (!spfPass && spfAligned)
                            return "SPF FAIL (aunque alineó)";
                          return "Falla combinada";
                        })();

                        const dkimDomain =
                          dkimRow?.dkim_domain ||
                          dkimRow?.ri_dkim_domain ||
                          sample?.dkim_domain;
                        const spfDomain =
                          spfRow?.spf_domain ||
                          spfRow?.ri_spf_domain ||
                          sample?.spf_domain;

                        const selector = dkimRow?.selector || "";

                        return (
                          <tr
                            key={recordid ?? idx}
                            className="border-b last:border-0 hover:bg-gray-50 transition-colors"
                          >
                            {/* RecordID */}
                            <td className="px-3 py-2 align-top font-mono">
                              <div className="font-medium text-slate-800">
                                {recordid}
                              </div>
                              <div className="text-[11px] text-slate-500">
                                {sample?.domain}
                              </div>
                              <div className="text-[11px] text-slate-500">
                                msgs: {sample?.count}
                              </div>
                            </td>

                            {/* Report Org */}
                            <td className="px-3 py-2 align-top">
                              {sample?.report_org || "—"}
                            </td>

                            {/* ASN Org */}
                            <td className="px-3 py-2 align-top">
                              <div
                                className="text-[11px] text-slate-700 truncate max-w-[10rem]"
                                title={sample?.asn_org}
                              >
                                {sample?.asn_org || "—"}
                              </div>
                            </td>

                            {/* Cloud Guess */}
                            <td className="px-3 py-2 align-top">
                              <div
                                className="text-[11px] text-slate-700 truncate max-w-[10rem]"
                                title={sample?.cloud_guess}
                              >
                                {sample?.cloud_guess || "—"}
                              </div>
                            </td>

                            {/* DKIM con colores */}
                            <td className="px-3 py-2 align-top space-y-1">
                              <div className="flex items-center gap-2">
                                <Badge tone={dkimPass ? "success" : "danger"}>
                                  result: {dkimRow?.result || "—"}
                                </Badge>
                                <Pill ok={dkimAligned} />
                              </div>
                              <div className="text-[11px] text-slate-500">
                                dkim_domain: {dkimDomain || "—"}
                              </div>
                            </td>

                            {/* SPF con colores */}
                            <td className="px-3 py-2 align-top space-y-1">
                              <div className="flex items-center gap-2">
                                <Badge tone={spfPass ? "success" : "danger"}>
                                  result: {spfRow?.result || "—"}
                                </Badge>
                                <Pill ok={spfAligned} />
                              </div>
                              <div className="text-[11px] text-slate-500">
                                spf_domain: {spfDomain || "—"}
                              </div>
                            </td>

                            {/* Selector con botón Copiar */}
                            <td className="px-3 py-2 align-top text-[11px]">
                              {selector ? (
                                <div className="flex items-center gap-2">
                                  <code className="px-1.5 py-0.5 bg-slate-50 border border-slate-200 rounded-md">
                                    {selector}
                                  </code>
                                  <CopyButton text={selector} />
                                </div>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>

                            {/* Causa en amarillo */}
                            <td className="px-3 py-2 align-top">
                              <Badge tone="warn">{cause}</Badge>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          className="px-3 py-3 text-center text-gray-500"
                          colSpan={8}
                        >
                          No se encontraron registros para esta IP en el rango
                          seleccionado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Footer utilitario opcional */}
              <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-[11px] text-slate-600">
                <div>
                  Consejo: valida el{" "}
                  <span className="font-medium">selector DKIM</span> y los
                  registros SPF del dominio del sobre-from.
                </div>
                <div className="flex gap-2">
                  <CopyButton
                    text={JSON.stringify(rows, null, 2)}
                    label="Copiar JSON"
                  />
                  <a
                    href={`${API_BASE}/detalle_ip_invalida_por_ip?ip=${encodeURIComponent(
                      ip,
                    )}&dominio=${encodeURIComponent(
                      dominio,
                    )}&desde=${encodeURIComponent(
                      desde,
                    )}&hasta=${encodeURIComponent(hasta)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[11px] px-2 py-1 rounded-md ring-1 ring-slate-200 hover:bg-slate-50"
                  >
                    Abrir API
                  </a>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
